"use client";

import {
  ConverterPanel,
  type ConverterStrings,
} from "@/components/tools/research-discovery/converter-panel";
import { useToolUi } from "@/components/tools/tool-ui";
import { convertEndnoteXml } from "@/lib/tool-kit/research-discovery/endnote";

/** English strings. A translated content file overrides these through `ui`. */
export const ENDNOTE_XML_CONVERTER_STRINGS = {
  inputLabel: "Your EndNote XML",
  inputPlaceholder:
    '<?xml version="1.0" encoding="UTF-8"?>\n<xml><records><record>…</record></records></xml>',
  inputHint:
    "Paste the XML or upload the file from EndNote's File → Export (Save as type: XML). It is converted in your browser — nothing is uploaded.",
  upload: "Upload an XML file",
  uploadLabel: "Choose an EndNote XML file to convert",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    '<?xml version="1.0" encoding="UTF-8"?>\n<xml><records>\n<record>\n<ref-type name="Journal Article">17</ref-type>\n<contributors><authors>\n<author><style face="normal" font="default" size="100%">Light, Janice</style></author>\n<author><style face="normal" font="default" size="100%">McNaughton, David</style></author>\n</authors></contributors>\n<titles><title><style face="normal" font="default" size="100%">From basic to applied research to improve outcomes for individuals who require augmentative and alternative communication</style></title></titles>\n<periodical><full-title>Augmentative and Alternative Communication</full-title><abbr-1>Augment Altern Commun</abbr-1></periodical>\n<pages>99-105</pages>\n<volume>30</volume>\n<number>2</number>\n<dates><year>2014</year><pub-dates><date>Jun</date></pub-dates></dates>\n<isbn>0743-4618</isbn>\n<electronic-resource-num>10.3109/07434618.2014.906498</electronic-resource-num>\n<keywords><keyword>eye tracking</keyword></keywords>\n</record>\n<record>\n<ref-type name="Book">6</ref-type>\n<contributors><authors><author>Cohen, Jacob</author></authors></contributors>\n<titles><title>Statistical power analysis for the behavioral sciences</title></titles>\n<edition>2nd</edition>\n<dates><year>1988</year></dates>\n<publisher>Lawrence Erlbaum Associates</publisher>\n<pub-location>Hillsdale, NJ</pub-location>\n</record>\n</records></xml>',
  fileRead: "Read {name} as {encoding}.",
  encodings: {
    utf8: "UTF-8",
    utf16: "UTF-16 — the converted file is saved as UTF-8",
    ansi: "Windows-1252, because it is not valid UTF-8 — check the accented letters",
  },
  emptyPrompt: "Paste or upload an EndNote XML export to convert it.",
  results: "Conversion",
  statFound: "Records found",
  statConverted: "Records converted",
  statDoi: "With a DOI",
  statWarnings: "Warnings",
  formatLabel: "Convert to",
  formats: { ris: "RIS", bibtex: "BibTeX", csl: "CSL JSON" },
  outputHeading: "Converted file",
  previewCut:
    "Showing the first {shown} of {total} lines. The download has the whole file.",
  copyOutput: "Copy",
  copied: "Copied!",
  download: "Download {file}",
  warningsHeading: "Things to check",
  warningsCut: "Showing the first {shown} of {total}.",
  warnings: {
    unknownType:
      "Reference type “{detail}” has no RIS equivalent here, so it was written as a generic reference.",
    noTitle: "This record has no title.",
    noAuthors: "This record has no authors.",
    doiUnreadable:
      "The electronic resource number “{detail}” does not contain a DOI, so it was kept as a note.",
    xmlProblem: "{detail}",
  },
  whereRecord: "Record {record}",
  whereFile: "Whole file",
  problems: {
    empty: "Paste or upload an EndNote XML export to convert it.",
    noRecords:
      "No <record> elements were found. Is this an EndNote XML export, from File → Export with Save as type set to XML?",
  },
  tableHeading: "Records",
  tableCut: "Showing the first {shown} of {total} records.",
  columns: { type: "Type", authors: "Authors", year: "Year", title: "Title" },
  etAl: "et al.",
  untitled: "Untitled",
} as const satisfies ConverterStrings;

export default function EndnoteXmlConverterWidget() {
  const strings = useToolUi(ENDNOTE_XML_CONVERTER_STRINGS);
  return (
    <ConverterPanel
      accept=".xml,text/xml,application/xml,text/plain"
      baseName="endnote-library"
      convert={convertEndnoteXml}
      idPrefix="exc"
      strings={strings}
    />
  );
}
